import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2, Download, Mail } from "lucide-react";

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [purchaseInfo, setPurchaseInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        setPurchaseInfo(response.data.metadata);
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus();
        }, pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      // Retry on server errors instead of immediately showing error
      if (attempts < maxAttempts - 1) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          pollPaymentStatus();
        }, pollInterval);
      } else {
        setStatus("error");
      }
    }
  };

  const handleDownload = () => {
    window.location.href = `${API}/download/${sessionId}`;
  };

  const packIds = purchaseInfo?.pack_ids?.split(",") || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20">
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-lg mx-auto text-center">
          {status === "checking" && (
            <div data-testid="checking-status">
              <Loader2 className="h-16 w-16 animate-spin text-emerald-900 mx-auto mb-6" />
              <h1 className="text-3xl font-serif font-bold text-slate-900 mb-4">Processing Payment...</h1>
              <p className="text-slate-600">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === "success" && (
            <div data-testid="success-status">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="h-10 w-10 text-emerald-700" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-slate-900 mb-4">Payment Successful!</h1>
              <p className="text-slate-600 mb-2">
                Thank you for your purchase!
              </p>
              {purchaseInfo?.email && (
                <p className="text-emerald-700 mb-6">
                  <Mail className="inline h-4 w-4 mr-1" />
                  Download link sent to: {purchaseInfo.email}
                </p>
              )}
              
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold mb-2">Your Purchase:</h3>
                <p className="text-amber-700 font-medium">{purchaseInfo?.product_title}</p>
                {packIds.length > 1 && (
                  <p className="text-sm text-slate-600 mt-1">{packIds.length} packs included</p>
                )}
              </div>

              <Button 
                className="bg-emerald-900 hover:bg-emerald-800 gold-text-outline text-lg py-6 px-8 w-full"
                onClick={handleDownload}
                data-testid="download-btn"
              >
                <Download className="mr-2 h-5 w-5" />
                Download Your Coloring Pages
              </Button>
              
              <div className="mt-6">
                <Link to="/coloring-pages" className="text-emerald-700 hover:text-emerald-900">
                  Back to Coloring Pages
                </Link>
              </div>
            </div>
          )}

          {status === "error" && (
            <div data-testid="error-status">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="h-10 w-10 text-red-700" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-slate-900 mb-4">Something Went Wrong</h1>
              <p className="text-slate-600 mb-6">
                We couldn't verify your payment. Please contact support if you believe this is an error.
              </p>
              <Link to="/coloring-pages">
                <Button className="bg-emerald-900 hover:bg-emerald-800">
                  Back to Coloring Pages
                </Button>
              </Link>
            </div>
          )}

          {status === "timeout" && (
            <div data-testid="timeout-status">
              <Loader2 className="h-16 w-16 text-amber-500 mx-auto mb-6" />
              <h1 className="text-3xl font-serif font-bold text-slate-900 mb-4">Payment Pending</h1>
              <p className="text-slate-600 mb-6">
                Your payment is still being processed. Please check your email for confirmation.
              </p>
              <Link to="/coloring-pages">
                <Button className="bg-emerald-900 hover:bg-emerald-800">
                  Back to Coloring Pages
                </Button>
              </Link>
            </div>
          )}

          {status === "expired" && (
            <div data-testid="expired-status">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="h-10 w-10 text-amber-700" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-slate-900 mb-4">Session Expired</h1>
              <p className="text-slate-600 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/coloring-pages">
                <Button className="bg-emerald-900 hover:bg-emerald-800">
                  Back to Coloring Pages
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
